import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  createProjectLabel,
  deleteProjectLabel,
  getProjectLabels,
  updateProjectLabel,
} from '../../../../services/projectLabelService';
import AddLabelModal from './popups/AddLabelModal';
import { DeleteModal } from './popups';
import { isStakeholder } from '../../../../utils/share';

const ProjectLabels = ({ projectId, isEditPermission }) => {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);

  const fetchLabels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getProjectLabels(projectId);
      setLabels(response?.data?.data || []);
    } catch (error) {
      const errMsg = error?.response?.data?.message || 'Failed to fetch labels';
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) fetchLabels();
  }, [projectId, fetchLabels]);

  const handleModalOpen = (label = null) => {
    setSelectedLabel(label);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedLabel(null);
  };

  const handleSubmit = async (values) => {
    try {
      if (selectedLabel) {
        const response = await updateProjectLabel(selectedLabel.id, values);
        toast.success(response?.data?.message);
      } else {
        const response = await createProjectLabel({ ...values, projectId: Number(projectId) });
        toast.success(response?.data?.message);
      }
      await fetchLabels();
      handleModalClose();
    } catch (error) {
      const errMsg = error?.response?.data?.message || 'An error occurred while saving the label';
      toast.error(errMsg);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedLabel) return;
    try {
      const response = await deleteProjectLabel(selectedLabel.id);
      toast.success(response?.data?.message);
      await fetchLabels();
    } catch (error) {
      const errMsg = error?.response?.data?.message || 'Failed to delete the label';
      toast.error(errMsg);
    } finally {
      setOpenDelete(false);
      setSelectedLabel(null);
    }
  };

  const canManage = isEditPermission && !isStakeholder();

  if (loading && !labels.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Labels
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
        {labels.map((label) => (
          <Stack
            key={label.id}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ border: '1px solid #e0e0e0', borderRadius: '16px', pl: 0.5, pr: canManage ? 0.5 : 1, py: 0.25 }}
          >
            <Chip
              size="small"
              label={label.name}
              sx={{ background: label.color, color: '#fff', fontWeight: 600 }}
            />
            {canManage && (
              <>
                <Tooltip title="Edit label" placement="top" arrow>
                  <IconButton size="small" onClick={() => handleModalOpen(label)}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete label" placement="top" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedLabel(label);
                      setOpenDelete(true);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        ))}

        {!labels.length && (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            No labels yet. Add one to make tasks stand out on the board.
          </Typography>
        )}

        {canManage && (
          <Tooltip title="Add label" placement="top" arrow>
            <IconButton size="small" onClick={() => handleModalOpen(null)}>
              <Add fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <AddLabelModal open={modalOpen} onClose={handleModalClose} onSubmit={handleSubmit} label={selectedLabel} />

      <DeleteModal
        open={openDelete}
        onClose={() => {
          setOpenDelete(false);
          setSelectedLabel(null);
        }}
        onDelete={handleConfirmDelete}
      />
    </Box>
  );
};

ProjectLabels.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isEditPermission: PropTypes.bool,
};

export default ProjectLabels;
